import "@/App.css";
import { Component } from "react";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";

import { PremiumProvider } from "@/context/PremiumContext";
import { CollectionProvider } from "@/context/CollectionContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { AuthProvider } from "@/context/AuthContext";
import RequireAuth from "@/components/auth/RequireAuth";
import RequireAdmin from "@/components/auth/RequireAdmin";
import { authRoutes } from "@/routes/auth";
import Layout from "@/components/Layout";
import Home from "@/pages/Home";
import LandingPage from "@/pages/LandingPage";
import { useAuth } from "@/context/AuthContext";
import Collections from "@/pages/Collections";
import CollectionDetail from "@/pages/CollectionDetail";
import CollectionSetup from "@/pages/CollectionSetup";
import Categories from "@/pages/Categories";
import CategoryBuilder from "@/pages/CategoryBuilder";
import CollectionBuilder from "@/pages/CollectionBuilder";
import Items from "@/pages/Items";
import ItemEntry from "@/pages/ItemEntry";
import ItemDetail from "@/pages/ItemDetail";
import InvoiceImport from "@/pages/InvoiceImport";
import Analytics from "@/pages/Analytics";
import Settings from "@/pages/Settings";
import Dashboard from "@/pages/Dashboard";
import Build from "@/pages/Build";
import Faq from "@/pages/Faq";
import About from "@/pages/About";
import RareValuesAll from "@/pages/RareValuesAll";
import Subscription from "@/pages/account/Subscription";
import MyAccount    from "@/pages/account/MyAccount";
import PlanPricing  from "@/pages/account/PlanPricing";
import ChangePassword from "@/pages/account/ChangePassword";
import TermsOfService    from "@/pages/legal/TermsOfService";
import PrivacyPolicy     from "@/pages/legal/PrivacyPolicy";
import RefundPolicy      from "@/pages/legal/RefundPolicy";
import SubscriptionTerms from "@/pages/legal/SubscriptionTerms";
import Contact           from "@/pages/legal/Contact";
import AdminDashboard    from "@/pages/admin/AdminDashboard";
import NotFound          from "@/pages/NotFound";

/**
 * Top-level ErrorBoundary — catches render/lifecycle exceptions from
 * anything inside <Routes> so a crashing page shows a readable message
 * instead of a blank white screen. React only supports class-based
 * boundaries, so this stays a class component. Everything above the
 * boundary (ThemeProvider, BrowserRouter, AuthProvider, etc.) is left
 * mounted so the theme, router, and session context stay live while the
 * user recovers.
 */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("[App ErrorBoundary]", error, info?.componentStack);
  }
  handleReload = () => {
    window.location.reload();
  };
  render() {
    if (this.state.error) {
      const msg = (this.state.error && (this.state.error.message || String(this.state.error))) || "Unknown error";
      return (
        <div
          role="alert"
          data-testid="app-error-boundary"
          className="min-h-screen flex items-center justify-center p-6 bg-[hsl(var(--bg))] text-[hsl(var(--fg))]"
        >
          <div className="max-w-lg w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--bg-elevated))] p-6 shadow-sm">
            <h1 className="text-lg font-semibold mb-2">Something went wrong.</h1>
            <p className="text-sm vp-muted mb-4">
              The page failed to render. The details below help us diagnose the crash — try reloading first.
            </p>
            <pre
              data-testid="app-error-message"
              className="text-xs whitespace-pre-wrap break-words max-h-64 overflow-auto p-3 rounded bg-[hsl(var(--muted))] border border-[hsl(var(--border))] mb-4"
            >
              {msg}
            </pre>
            <button
              type="button"
              onClick={this.handleReload}
              data-testid="app-error-reload"
              className="vp-btn vp-btn-primary vp-btn-sm"
            >
              Reload
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

/**
 * NavigationBridge — exposes react-router's `navigate` on
 * `window.__cdiNavigate` so non-component modules (notably the 401
 * interceptor in `lib/api.js`) can trigger client-side navigation
 * without doing a full-page reload. Mounted inside `<BrowserRouter>`.
 * The bridge only runs `navigate`, never renders anything.
 */
function NavigationBridge() {
  const navigate = useNavigate();
  useEffect(() => {
    window.__cdiNavigate = (to, opts) => navigate(to, opts);
    return () => { if (window.__cdiNavigate) delete window.__cdiNavigate; };
  }, [navigate]);
  return null;
}

/**
 * LandingOrDashboard — public gate for the root path. Both anonymous
 * visitors AND authenticated users now see the marketing LandingPage;
 * the auto-redirect to `/dashboard` for logged-in users was removed in
 * the Oct-2026 routing refactor so `/` is a stable, non-redirecting
 * destination. Only explicit admin navigation routes to `/dashboard`.
 */
function LandingOrDashboard() {
  const { user } = useAuth();
  if (user) return <Navigate to="/dashboard" replace />;
  return <LandingPage />;
}

function App() {
  return (
    <div className="App">
      <ThemeProvider>
        <BrowserRouter>
          <AuthProvider>
            <PremiumProvider>
              <CollectionProvider>
                <NavigationBridge />
                <ErrorBoundary>
                  <Routes>
                  {/* Public — legal + contact page shells (Phase 5). Placed
                      outside <RequireAuth /> so unauthenticated visitors
                      (footer clicks from the login screen) can view them. */}
                  <Route path="/legal/terms"        element={<TermsOfService />} />
                  <Route path="/legal/privacy"      element={<PrivacyPolicy />} />
                  <Route path="/legal/refund"       element={<RefundPolicy />} />
                  <Route path="/legal/subscription" element={<SubscriptionTerms />} />
                  <Route path="/legal/contact"      element={<Contact />} />
                  <Route path="/contact"            element={<Contact />} />

                  {/* Account — informational overview page. Protected via
                      RequireAuth so only signed-in users can view it. */}
                  <Route path="/account" element={<RequireAuth><MyAccount /></RequireAuth>} />
                  <Route path="/account/plan-pricing" element={<RequireAuth><PlanPricing /></RequireAuth>} />
                  <Route path="/account/change-password" element={<RequireAuth><ChangePassword /></RequireAuth>} />

                  {/* Public — auth screens (login / signup / magic / verify /
                      reset / session-expired). */}
                  <Route path="/auth">
                    {authRoutes.map((r) => (
                      <Route key={r.path} path={r.path} element={r.element} />
                    ))}
                    {/* /auth/signin alias — the public LandingPage CTAs
                        and marketing copy use "signin" terminology, but
                        the registered auth route is `/auth/login`.
                        Redirect preserves both naming schemes without
                        touching the Login page or auth flow. */}
                    <Route path="signin" element={<Navigate to="/auth/login" replace />} />
                  </Route>

                  {/* Public — marketing landing page at root. Authenticated
                      visitors get client-side redirected to /dashboard
                      inside <LandingOrDashboard />. Must come BEFORE the
                      protected tree below so the matcher resolves the
                      index to LandingOrDashboard rather than the Layout. */}
                  <Route path="/" element={<LandingOrDashboard />} />

                  {/* Protected — everything below requires a live session.
                      Pathless parent + absolute child paths so the
                      landing route owns the index at `/`. */}
                  <Route element={<RequireAuth><Layout /></RequireAuth>}>
                    <Route path="/home" element={<Home />} />

                    <Route path="/collections" element={<Collections />} />
                    <Route path="/collections/create" element={<Collections />} />
                    <Route path="/collections/builder" element={<CollectionBuilder />} />
                    <Route path="/collections/:id" element={<CollectionDetail />} />
                    <Route path="/collections/:id/setup" element={<CollectionSetup />} />

                    <Route path="/categories" element={<Categories />} />
                    <Route path="/categories/new" element={<CategoryBuilder />} />
                    <Route path="/categories/:id/edit" element={<CategoryBuilder />} />

                    <Route path="/items" element={<Items />} />
                    <Route path="/items/new" element={<ItemEntry />} />
                    <Route path="/items/:id" element={<ItemDetail />} />
                    <Route path="/items/:id/edit" element={<ItemEntry />} />

                    <Route path="/invoice" element={<InvoiceImport />} />
                    <Route path="/analytics" element={<Analytics />} />
                    <Route path="/settings" element={<Settings />} />
                    <Route path="/settings/rare-values" element={<RequireAdmin><RareValuesAll /></RequireAdmin>} />
                    <Route path="/account/subscription" element={<Subscription />} />
                    <Route path="/dashboard" element={<Dashboard />} />
                    <Route path="/build" element={<Build />} />
                    <Route path="/faq" element={<Faq />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/admin" element={<RequireAdmin><AdminDashboard /></RequireAdmin>} />

                    {/* Legacy redirects (keep old links working) */}
                    <Route path="/collection" element={<Navigate to="/items" replace />} />
                    <Route path="/collection/:id" element={<LegacyItemRedirect />} />
                    <Route path="/scan" element={<Navigate to="/items/new" replace />} />
                    <Route path="/valuation" element={<Navigate to="/dashboard" replace />} />
                    <Route path="/premium" element={<Navigate to="/settings" replace />} />
                  </Route>

                  {/* Short-URL redirects + catch-all 404.
                      These stay OUTSIDE the authenticated <Layout>
                      wrapper so unknown or logged-out visits don't trip
                      the auth gate before hitting their target. */}
                  <Route path="/pricing" element={<Navigate to="/account/plan-pricing" replace />} />
                  <Route path="/privacy" element={<Navigate to="/legal/privacy" replace />} />
                  <Route path="*" element={<NotFound />} />
                </Routes>
                </ErrorBoundary>
              </CollectionProvider>
            </PremiumProvider>
          </AuthProvider>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </ThemeProvider>
    </div>
  );
}

// Helper: /collection/:id (old item detail URL) → /items/:id
function LegacyItemRedirect() {
  const params = new URLSearchParams(window.location.search);
  const id = window.location.pathname.split("/").pop();
  return <Navigate to={`/items/${id}?${params.toString()}`} replace />;
}

export default App;
